const T = require('prop-types');
const { push: Push } = require('connected-react-router');
const { makeActionCreator } = require('../utils/redux-helpers');
const { SIGNUP: Types } = require('../action-types');
const GetQueryParams = require('utils/get-query-params');

const {
    Context: SignupContext,
    Identify,
    Authenticate,
    Profile,
    Complete,
    SignupCompleted,
    ProfilePicture
} = require('../domain/signup');

const AuthConstants = require('services/auth-management/constants');
const RoutingTable = require('services/auth-management/routing-table');
const AuthDebug = require('services/auth-management/debug');
const SignupState = require('services/auth-management/signup-state');
const AuthStateTransitions = require('services/auth-management/state-transitions');

const internals = {};

module.exports = (context) => {

    const api       = context.api.nearpeer;
    const selectors = context.selectors.all;
    const actions   = context.actions;
    const redux     = context.redux.hooks;

    const identify = {
        complete: makeActionCreator(Types.IDENTIFY.COMPLETE, Identify, {
            async: async (payload) => {

                redux.dispatch(actions.signup.initContext(payload));

                const signupRes = await api.signup.startSignup(payload);

                const { email, schoolId } = payload;

                await redux.dispatch(actions.signup.checkSchoolSSO({
                    email,
                    schoolId
                }));

                return signupRes;
            },
            after: ({ payload, state }) => {

                const {
                    request: { email },
                    result: {
                        data: {
                            s: schoolId,
                            r: roleId
                        }
                    }
                } = payload;

                const { authMethod } = selectors.getSignupContext(state);

                redux.dispatch(actions.signup.updateContext({
                    email,
                    schoolId,
                    roleId
                }));

                if (authMethod === AuthConstants.authFlow.methods.sso) {
                    return Push(AuthConstants.authFlow.routes.signup.sso);
                }

                return Push(AuthConstants.authFlow.routes.signup.verifyEmail);
            }
        }),
        resendEmail: makeActionCreator(Types.IDENTIFY.RESEND_EMAIL, { email: T.string, schoolId: T.number }, {
            async: api.signup.resendEmail,
            after: () => {

                return redux.dispatch(actions.alerts.notification.push({
                    message: 'Verification Email resent'
                }));
            }
        }),
        failure: makeActionCreator(Types.IDENTIFY.FAILURE, {})
    };

    const authenticate = {
        checkPassword: makeActionCreator(Types.AUTHENTICATE.CHECK_PASSWORD, { verificationToken: T.string }, {
            async: api.signup.checkPassword
        }),
        complete: makeActionCreator(Types.AUTHENTICATE.COMPLETE, Authenticate, {
            async: async (payload) => {

                if (payload.password !== null) {
                    await api.signup.storePassword({
                        password: payload.password,
                        verificationToken: payload.verificationToken
                    });

                    const signupContext = selectors.getSignupContext(redux.getState());
                    let passwordContext = signupContext;

                    if (signupContext.authMethod === AuthConstants.authFlow.methods.mfa && signupContext.email) {
                        let schoolCheckResult;

                        try {
                            schoolCheckResult = await api.signup.checkSchoolSSO({
                                email: signupContext.email
                            });
                        }
                        catch (err) {
                            schoolCheckResult = null;
                        }

                        passwordContext = schoolCheckResult
                            ? RoutingTable.buildSchoolCheckFlow({
                                email: signupContext.email,
                                schoolCheckResult
                            }).authMode
                            : signupContext;
                    }

                    return {
                        email: signupContext.email,
                        passwordContext
                    };
                }
            },
            after: ({ dispatch, payload }) => {

                const destination = RoutingTable.resolve(RoutingTable.ROUTING_KINDS.passwordComplete, {
                    context: payload.result?.passwordContext,
                    isAuthenticated: selectors.getIsAuthenticated(redux.getState()),
                    verificationToken: payload.request.verificationToken
                });

                return Push(destination);
            }
        }),
        completeSSO: makeActionCreator(Types.AUTHENTICATE.COMPLETE_SSO, Authenticate, {
            async: async () => undefined,
            after: ({ dispatch, state }) => {

                dispatch(actions.signup.updateContext({
                    agreeToTerms: true
                }));

                return Push(RoutingTable.getIncompleteSignupRoute({
                    signupContext: {
                        ...selectors.getSignupContext(state),
                        agreeToTerms: true
                    }
                }));
            }
        })
    };

    const profile = {
        fetchSignupProgress: makeActionCreator(Types.PROFILE.FETCH_SIGNUP_PROGRESS, { verificationToken: T.string }, {
            async: api.signup.fetchSignupProgress
        }),
        updateSignupProgress: makeActionCreator(Types.PROFILE.UPDATE_SIGNUP_PROGRESS, { verificationToken: T.string, signupDetails: Profile }, {
            async: api.signup.updateSignupProgress
        }),
        updateSignupProgressProfilePic: makeActionCreator(Types.PROFILE.UPDATE_SIGNUP_PROGRESS_PROFILE_PICTURE, { verificationToken: T.string, pictureDetails: ProfilePicture }, {
            async: api.signup.updateSignupProgressProfilePic
        }),
        updatePreferencesPresignup: makeActionCreator(Types.PROFILE.UPDATE_USER_PREFERENCES_PRE_SIGNUP, { verificationToken: T.string, prefs: T.object }, {
            async: api.signup.updateUserPreferencesPreSignup
        }),
        complete: makeActionCreator(Types.PROFILE.COMPLETE, Profile, {
            after: ({ state }) => {

                return actions.signup.complete(
                    selectors.getSignupDetailsForCompletion(state)
                );
            }
        }),
        goBack: makeActionCreator(Types.PROFILE.GO_BACK, Profile, {
            after: () => Push(AuthConstants.authFlow.routes.signup.password)
        })
    };

    return {
        initContext: makeActionCreator(Types.CONTEXT_INIT, SignupContext),
        updateContext: makeActionCreator(Types.CONTEXT_UPDATE, SignupContext),
        verifyEmail: makeActionCreator(Types.VERIFY_EMAIL, { verificationToken: T.string }, {
            async: api.signup.verifyEmail
        }),
        checkSchoolSSO: makeActionCreator(Types.SCHOOL_CHECK, { email: T.string, schoolId: T.number }, {
            async: (payload) => AuthDebug.captureApiCall({
                name: 'signup.checkSchoolSSO',
                request: payload,
                func: () => api.signup.checkSchoolSSO(payload)
            })
        }),
        identify,
        authenticate,
        profile,
        complete: makeActionCreator(Types.COMPLETE, Complete, {
            requestPropTransform: (rawPayload, { state }) => internals.withSignupCompletionContext({
                signupDetails: rawPayload,
                state,
                selectors
            }),
            async: async (prefsAndDetails) => {

                const {
                    preferences: {
                        phone,
                        phoneCountry,
                        profileEmail,
                        ...preferences
                    },
                    ...signupDetails
                } = prefsAndDetails;

                delete signupDetails.agreeToTerms;
                delete signupDetails.agreedToTerms;

                const signupContext = selectors.getSignupContext(redux.getState());
                const completionResult = await api.signup.completeSignup({
                    phone: phone || undefined,
                    phoneCountry: phoneCountry || undefined,
                    profileEmail: profileEmail || undefined,
                    ...signupDetails
                });

                RoutingTable.assertMfaAllowedForAuthContext({
                    authContext: selectors.getSignupContext(redux.getState()),
                    result: completionResult
                });

                return {
                    ...completionResult,
                    authenticatedEmail: signupContext.email,
                    pendingPreferences: preferences
                };
            },
            after: async ({ dispatch, payload }) => {

                const pendingPreferences = payload.result?.pendingPreferences
                    || internals.getPendingPreferences(payload.request?.preferences);

                if (RoutingTable.isPendingMfaStatus(payload.result?.status)) {
                    RoutingTable.routePendingMfa({
                        currentContext: {
                            ...selectors.getSignupContext(redux.getState()),
                            ...payload.request,
                            hasCompletedProfile: true,
                            mfaPurpose: AuthConstants.authFlow.sources.signupCompletionMfa,
                            pendingPreferences
                        },
                        dispatch,
                        response: payload.result,
                        source: AuthConstants.authFlow.sources.signupCompletionMfa,
                        updateContext: actions.signup.updateContext
                    });
                    return undefined;
                }

                if (!selectors.getIsAuthenticated(redux.getState())) {
                    throw new Error('Cannot complete signup without the returned auth token');
                }

                await dispatch(actions.profileManagement.updateUserPreferences(pendingPreferences));

                dispatch(actions.signup.signupCompleted({ SignupCompleted: true }));
                AuthStateTransitions.completeSuccessfulAuthentication(
                    payload.result?.authenticatedEmail
                        || payload.request?.email
                        || selectors.getSignupContext(redux.getState())?.email
                );

                return dispatch(actions.auth.bootstrapMainApp({
                    redirect: payload.request?.redirect
                        || RoutingTable.REDIRECTS.welcome,
                    ...(payload.request?.redirectTarget ? {
                        target: payload.request.redirectTarget
                    } : {}),
                    schoolId: payload.result?.schoolId || payload.request?.schoolId
                }));
            }
        }),
        signupCompleted: makeActionCreator(Types.SIGNUP_COMPLETED, SignupCompleted)
    };
};

internals.withSignupCompletionContext = ({
    signupDetails = {},
    state,
    selectors
}) => {

    const signupContext = selectors.getSignupContext(state) || {};
    const authDetails = selectors.getSignupAuthenticateDetails(state) || {};
    const {
        v,
        email,
        s,
        r
    } = GetQueryParams();

    return {
        ...signupContext,
        ...authDetails,
        ...signupDetails,
        verificationToken: signupDetails.verificationToken
            || signupContext.verificationToken
            || authDetails.verificationToken
            || v,
        schoolId: signupDetails.schoolId
            || signupContext.schoolId
            || (s ? Number(s) : undefined),
        roleId: signupDetails.roleId
            || signupContext.roleId
            || (r ? Number(r) : undefined),
        email: signupDetails.email
            || signupContext.email
            || email
    };
};

internals.getPendingPreferences = (preferences = {}) => {

    const {
        phone,
        phoneCountry,
        profileEmail,
        ...pendingPreferences
    } = preferences || {};

    return pendingPreferences;
};
